// CanvasView マウント時に RealtimeClient を作成し、サーバからの op を doc store へ反映する。
// authMode === "mock" のときは mock realtime（自分が送った op をエコーバック）を使い、
// それ以外では WebSocket 経由で実サーバに接続する。

import { useEffect, useMemo, useState } from "react";
import { getWebConfig } from "../env";
import { createMockRealtimeClient } from "./mock-realtime";
import { createWebSocketRealtimeClient } from "./websocket-realtime";
import { useCanvasDocStore } from "../features/canvas/store/canvas-doc-store";
import { useCanvasViewersStore } from "../features/canvas/store/canvas-viewers-store";
import type { RealtimeClient } from "./realtime-client";
import type { CanvasObject, ServerMessage } from "@refinement-canvas/domain";
import { useCanvasApi } from "../lib/canvas-api";

interface State {
  readonly client: RealtimeClient | null;
  readonly status: "idle" | "connecting" | "connected" | "error";
  readonly error?: Error | undefined;
}

export function useRealtimeConnection(canvasId: string): State {
  const config = getWebConfig();
  const canvasApi = useCanvasApi();
  const onServerCommit = useCanvasDocStore((s) => s.onServerCommit);
  const onServerReject = useCanvasDocStore((s) => s.onServerReject);
  const loadSnapshot = useCanvasDocStore((s) => s.loadSnapshot);
  const setViewers = useCanvasViewersStore((s) => s.setViewers);
  const setCursor = useCanvasViewersStore((s) => s.setCursor);
  const resetViewers = useCanvasViewersStore((s) => s.reset);

  // クライアント自身は再生成したくないので useMemo + キーで安定させる
  const client = useMemo<RealtimeClient>(() => {
    if (config.authMode === "mock") {
      return createMockRealtimeClient();
    }
    return createWebSocketRealtimeClient({ websocketUrl: config.realtimeWebSocketUrl });
  }, [config.authMode, config.realtimeWebSocketUrl]);

  const [status, setStatus] = useState<State["status"]>("idle");
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let cancelled = false;
    setStatus("connecting");

    // サーバからの message を doc store / viewers store に橋渡しする
    const unsubscribe = client.onMessage((message: ServerMessage) => {
      switch (message.type) {
        case "op.commit":
          onServerCommit(message.op, message.serverSeq);
          return;
        case "op.reject":
          onServerReject(message.clientOpId);
          return;
        case "bootstrap.snapshot":
          loadSnapshot({
            canvasId,
            serverSeq: message.serverSeq,
            objects: message.objects as CanvasObject[],
          });
          return;
        case "viewer.snapshot":
          setViewers(message.viewers);
          return;
        case "viewer.cursor":
          if (message.cursor) {
            setCursor(message.userId, {
              xRatio: message.cursor.xRatio,
              yRatio: message.cursor.yRatio,
              updatedAt: Date.now(),
            });
          } else {
            setCursor(message.userId, null);
          }
          return;
        case "op.transient":
          // 一時 op（ドラッグ中の中間座標 / IME カーソル / ペン途中）の反映は
          // canvas 表示側のレイヤで処理する想定。本実装では transient はサーバ側で
          // 中継するのみで、クライアントは別チャンネル（カーソル / ペン途中）として扱う。
          return;
        case "resync.required":
          // サーバ再起動 / シーケンス欠落時は REST bootstrap で最新スナップショットを取り直す。
          // 画面遷移や canvas 切替が走った後にレスポンスが返るケースを考慮し、
          // useEffect の cleanup で立てた cancelled フラグと、レスポンスの canvasId が
          // 現在表示中の canvasId と一致しているかを二重確認してから store を上書きする。
          console.warn("[realtime] resync.required:", message.reason);
          void (async () => {
            try {
              const data = await canvasApi.fetchCanvasBootstrap(canvasId);
              if (cancelled) return;
              if (data.canvasId !== canvasId) return;
              loadSnapshot({
                canvasId: data.canvasId,
                serverSeq: data.serverSeq,
                objects: [...data.objects],
                meta: data.meta,
              });
            } catch (e) {
              if (cancelled) return;
              console.warn("[realtime] resync 後の bootstrap に失敗", e);
            }
          })();
          return;
      }
    });

    void (async () => {
      try {
        // required 環境ではチケットを発行し、WebSocket の ?ticket= に付与する。
        // チケット発行に失敗した場合は接続できないため、エラー扱いとする。
        let ticket: string | undefined;
        if (config.authMode !== "mock") {
          const session = await canvasApi.issueRealtimeSession(canvasId);
          ticket = session.token;
        }
        await client.connect(canvasId, ticket);
        if (cancelled) return;
        setStatus("connected");
      } catch (e) {
        if (cancelled) return;
        setStatus("error");
        setError(e instanceof Error ? e : new Error(String(e)));
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
      client.disconnect();
      resetViewers();
    };
  }, [
    canvasApi,
    canvasId,
    client,
    config.authMode,
    loadSnapshot,
    onServerCommit,
    onServerReject,
    setViewers,
    setCursor,
    resetViewers,
  ]);

  return { client, status, error };
}
